import { Component, ViewChild, OnInit, OnDestroy, ElementRef } from "@angular/core";
import {
  SearchFilter,
  IFilterResponse,
} from "../../../infrastructure/iFilter-component";
import { MatDialog } from "@angular/material";
import { SearchFilterComponent } from "../../search-filter/search-filter.component";
import { MenuService } from "../../../services/menu.service";
import { AdmissionSearchItem } from "../../../model/admission-search-item";
import { PagerControlComponent } from "../../controls/pager/pager-control.component";
import { Router } from "@angular/router";
import { MedicalAppointmenObservationDialogComponent } from "../../dialogs/medical-appointment-observation/medical-appointment-observation-dialog.component";
import { LogViewDialogComponent } from "../../dialogs/log-view/log-view-dialog.component";
import { AuthService } from "../../../services/auth.service";
import { MedicalAppointmentCancel } from "../../../model/medical-appointment-cancel";
import { DialogCancelMedicalAppointmentComponent } from "../../dialogs/medical-appointment-cancel/dialog-medical-appointment-cancel.component";
import { EnvironmentPresetService } from "../../../services/environment-preset/environment-preset.service";
import { SelectionModel } from "@angular/cdk/collections";
import { AdmissionService } from "../../../services/admission.service";
import { NotificationToastService } from "../../../services/notification-toast.service";
import { MedicalAppointmenChangeDateDialogComponent } from "../../dialogs/medical-appointment-change-date/medical-appointment-change-date-dialog.component";
import { moveItemInArray } from "@angular/cdk/drag-drop";
import { DatePipe } from "@angular/common";

interface ColumnDefinition {
  field: string;
  title: string;
  visible: boolean;
  order: number;
  width: number;
}

@Component({
  selector: "app-admission-search-component",
  templateUrl: "./admission-search.component.html",
  styleUrls: ["./admission-search.component.css"],
  providers: [DatePipe],
})
export class AdmissionSearchComponent implements OnInit, OnDestroy {
  canCancelAdmited: boolean;
  canChangeAppointmentDate: boolean;
  loaded: boolean;
  loadingAction: boolean;
  loadingMasiveAction: boolean;
  searchFilters: SearchFilter[];

  @ViewChild("pager", { static: true }) pager: PagerControlComponent;
  @ViewChild("searchFilter", { static: false }) searchFilter: SearchFilterComponent;
  @ViewChild("gridScrollContainer", { static: false }) gridScrollContainer: ElementRef<HTMLDivElement>;

  selection = new SelectionModel<AdmissionSearchItem>(true, []);

  private sessionColumnsKey = "admission-search.columns.v1";
  private sessionSortKey = "admission-search.sort.v1";
  private sessionColumnWidthsKey = "admission-search.column-widths.v1";

  columnsDefinition: ColumnDefinition[] = [
    { field: "date",                       title: "Fecha",            visible: true, order: 0,  width: 150 },
    { field: "receptionDate",              title: "Recepcionado",     visible: true, order: 1,  width: 150 },
    { field: "stateName",                  title: "Estado",           visible: true, order: 2,  width: 110 },
    { field: "isOverAppointment",          title: "Es sobreturno",    visible: true, order: 3,  width: 120 },
    { field: "isReAppointmentYesNo",       title: "Es recitado",      visible: true, order: 4,  width: 110 },
    { field: "patientLasNameFirstName",    title: "Paciente",         visible: true, order: 5,  width: 200 },
    { field: "protocol",                   title: "Protocolo",        visible: true, order: 6,  width: 150 },
    { field: "practiceRegionName",         title: "Práctica",         visible: true, order: 7,  width: 180 },
    { field: "equipmentName",              title: "Equipo",           visible: true, order: 8,  width: 150 },
    { field: "physicianLastNameFirstName", title: "Médico actuante",  visible: true, order: 9,  width: 180 },
    { field: "entityName",                 title: "Entidad",          visible: true, order: 10, width: 180 },
  ];

  get availableColumns(): ColumnDefinition[] {
    return this.columnsDefinition;
  }

  get orderedColumnsDefinition(): ColumnDefinition[] {
    return this.columnsDefinition
      .filter(c => c.visible)
      .sort((a, b) => a.order - b.order);
  }

  dataSource: AdmissionSearchItem[] = [];
  allItems: AdmissionSearchItem[] = [];
  columnFilters: { [field: string]: string } = {};

  private sortField: string = null;
  private sortDirection: "asc" | "desc" = "asc";
  private dragSourceField: string = null;
  private resizeListeners: { move: EventListener; up: EventListener } = null;

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private notificationToastService: NotificationToastService,
    private admissionService: AdmissionService,
    private authService: AuthService,
    private environmentPresetService: EnvironmentPresetService,
    private menuService: MenuService,
    private datePipe: DatePipe
  ) {
    this.menuService.setMenuTitle("Admisión", "consulta");
    this.canCancelAdmited = this.authService.canCancelAdmited();
    this.canChangeAppointmentDate = this.authService.canChangeAppointmentDate();
  }

  ngOnInit() {
    this.restoreColumns();
    this.restoreSort();
    this.restoreColumnWidths();
  }

  ngOnDestroy() {
    this.removeResizeListeners();
  }

  // ── Selection ─────────────────────────────────────────────────────────────

  isAllSelected() {
    if (this.selection.selected && this.pager.getAllItems()) {
      return this.selection.selected.length === this.pager.getAllItems().length;
    }
    return false;
  }

  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.pager.getAllItems().forEach((row) => this.selection.select(row));
  }

  // ── Column visibility ──────────────────────────────────────────────────────

  isColumnVisible(field: string): boolean {
    const col = this.columnsDefinition.find(c => c.field === field);
    return col ? col.visible : false;
  }

  setColumnVisibility(field: string, visible: boolean) {
    const col = this.columnsDefinition.find(c => c.field === field);
    if (col) {
      col.visible = visible;
      this.saveColumns();
      this.fitVisibleColumnsToContainer();
    }
  }

  showAllColumns() {
    this.columnsDefinition.forEach(c => (c.visible = true));
    this.saveColumns();
    this.fitVisibleColumnsToContainer();
  }

  private saveColumns() {
    const state = this.columnsDefinition.map(c => ({ field: c.field, visible: c.visible, order: c.order }));
    sessionStorage.setItem(this.sessionColumnsKey, JSON.stringify(state));
  }

  private restoreColumns() {
    const raw = sessionStorage.getItem(this.sessionColumnsKey);
    if (!raw) { return; }
    try {
      const state: { field: string; visible: boolean; order: number }[] = JSON.parse(raw);
      state.forEach(s => {
        const col = this.columnsDefinition.find(c => c.field === s.field);
        if (col) { col.visible = s.visible; col.order = s.order; }
      });
    } catch { }
  }

  // ── Sort ──────────────────────────────────────────────────────────────────

  sortByColumn(field: string) {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortField = field;
      this.sortDirection = "asc";
    }
    this.saveSort();
    this.refreshGrid();
  }

  getSortIcon(field: string): string {
    if (this.sortField !== field) { return "unfold_more"; }
    return this.sortDirection === "asc" ? "arrow_upward" : "arrow_downward";
  }

  private sortAllData(items: AdmissionSearchItem[]): AdmissionSearchItem[] {
    if (!this.sortField) { return items; }
    const dir = this.sortDirection === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
      const va = this.getColumnFilterValue(a, this.sortField);
      const vb = this.getColumnFilterValue(b, this.sortField);
      return va < vb ? -dir : va > vb ? dir : 0;
    });
  }

  private saveSort() {
    sessionStorage.setItem(this.sessionSortKey, JSON.stringify({ field: this.sortField, direction: this.sortDirection }));
  }

  private restoreSort() {
    const raw = sessionStorage.getItem(this.sessionSortKey);
    if (!raw) { return; }
    try {
      const s = JSON.parse(raw);
      this.sortField = s.field;
      this.sortDirection = s.direction;
    } catch { }
  }

  // ── Column filters ────────────────────────────────────────────────────────

  onColumnFilterInput(field: string, value: string) {
    this.columnFilters[field] = value;
    this.refreshGrid();
  }

  private applyColumnFilters(items: AdmissionSearchItem[]): AdmissionSearchItem[] {
    return items.filter(item =>
      Object.keys(this.columnFilters).every(field => {
        const filterVal = this.normalizeFilterValue(this.columnFilters[field]);
        if (!filterVal) { return true; }
        const cellVal = this.normalizeFilterValue(this.getColumnFilterValue(item, field));
        return cellVal.includes(filterVal);
      })
    );
  }

  private getColumnFilterValue(item: AdmissionSearchItem, field: string): string {
    switch (field) {
      case "date":                       return this.datePipe.transform(item.DateTime, "dd/MM/yyyy HH:mm") || "";
      case "receptionDate":              return this.datePipe.transform(item.ReceptionDate, "dd/MM/yyyy HH:mm") || "";
      case "stateName":                  return item.StateName || "";
      case "isOverAppointment":          return item.IsOverAppointment ? "sí" : "no";
      case "isReAppointmentYesNo":       return item.IsReAppointment ? "sí" : "no";
      case "patientLasNameFirstName":    return item.PatientLastNameFirstName || "";
      case "protocol":                   return item.Protocol || "";
      case "practiceRegionName":         return item.PracticeRegionName || "";
      case "equipmentName":              return item.EquipmentName || "";
      case "physicianLastNameFirstName": return item.PhysicianLastNameFirstName || "";
      case "entityName":                 return item.EntityName || "";
      default:                           return "";
    }
  }

  private normalizeFilterValue(value: string): string {
    if (!value) { return ""; }
    return value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  // ── Refresh ───────────────────────────────────────────────────────────────

  private refreshGrid() {
    const filtered = this.applyColumnFilters(this.allItems);
    const sorted = this.sortAllData(filtered);
    this.pager.setItems(sorted);
  }

  // ── Column resize ─────────────────────────────────────────────────────────

  getColumnWidth(field: string): number {
    const col = this.columnsDefinition.find(c => c.field === field);
    return col ? col.width : 150;
  }

  startResize(event: MouseEvent, field: string) {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const col = this.columnsDefinition.find(c => c.field === field);
    const startWidth = col ? col.width : 150;

    this.removeResizeListeners();

    const moveHandler: EventListener = (e: MouseEvent) => {
      const newWidth = Math.max(50, startWidth + (e.clientX - startX));
      if (col) { col.width = newWidth; }
    };
    const upHandler: EventListener = () => {
      this.removeResizeListeners();
      this.saveColumnWidths();
    };

    document.addEventListener("mousemove", moveHandler);
    document.addEventListener("mouseup", upHandler);
    this.resizeListeners = { move: moveHandler, up: upHandler };
  }

  private removeResizeListeners() {
    if (this.resizeListeners) {
      document.removeEventListener("mousemove", this.resizeListeners.move);
      document.removeEventListener("mouseup", this.resizeListeners.up);
      this.resizeListeners = null;
    }
  }

  private saveColumnWidths() {
    const widths: { [field: string]: number } = {};
    this.columnsDefinition.forEach(c => (widths[c.field] = c.width));
    sessionStorage.setItem(this.sessionColumnWidthsKey, JSON.stringify(widths));
  }

  private restoreColumnWidths() {
    const raw = sessionStorage.getItem(this.sessionColumnWidthsKey);
    if (!raw) { return; }
    try {
      const widths: { [field: string]: number } = JSON.parse(raw);
      this.columnsDefinition.forEach(c => {
        if (widths[c.field] !== undefined) { c.width = widths[c.field]; }
      });
    } catch { }
  }

  private fitVisibleColumnsToContainer() {
    setTimeout(() => {
      const container = this.gridScrollContainer && this.gridScrollContainer.nativeElement;
      const visibleColumns = this.orderedColumnsDefinition;
      if (!container || visibleColumns.length === 0) { return; }

      const fixedColumnsWidth = 96;
      const availableWidth = container.clientWidth - fixedColumnsWidth;
      if (availableWidth <= 0) { return; }

      const totalWidth = visibleColumns.reduce((sum, col) => sum + col.width, 0);
      if (totalWidth <= 0) { return; }

      const scale = availableWidth / totalWidth;
      const minWidth = Math.max(50, Math.floor(availableWidth / visibleColumns.length));
      let assignedWidth = 0;

      visibleColumns.forEach((col, index) => {
        const remainingColumns = visibleColumns.length - index - 1;
        const desiredWidth = Math.max(minWidth, Math.floor(col.width * scale));
        const maxWidth = availableWidth - assignedWidth - (remainingColumns * minWidth);
        const nextWidth = index === visibleColumns.length - 1
          ? availableWidth - assignedWidth
          : Math.min(desiredWidth, maxWidth);

        col.width = nextWidth;
        assignedWidth += nextWidth;
      });

      this.saveColumnWidths();
    });
  }

  // ── Column drag/drop ──────────────────────────────────────────────────────

  onHeaderDragStart(event: DragEvent, field: string) {
    this.dragSourceField = field;
    event.dataTransfer.effectAllowed = "move";
  }

  onHeaderDragOver(event: DragEvent) {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }

  onHeaderDrop(event: DragEvent, targetField: string) {
    event.preventDefault();
    if (!this.dragSourceField || this.dragSourceField === targetField) { return; }
    const visible = this.orderedColumnsDefinition;
    const fromIdx = visible.findIndex(c => c.field === this.dragSourceField);
    const toIdx = visible.findIndex(c => c.field === targetField);
    if (fromIdx < 0 || toIdx < 0) { return; }
    moveItemInArray(visible, fromIdx, toIdx);
    visible.forEach((c, i) => {
      const col = this.columnsDefinition.find(x => x.field === c.field);
      if (col) { col.order = i; }
    });
    this.saveColumns();
    this.dragSourceField = null;
  }

  onHeaderDragEnd() {
    this.dragSourceField = null;
  }

  // ── Lifecycle / search ────────────────────────────────────────────────────

  onStartSearch() {}

  selectedPageIndexChange(pagedItems: any[]) {
    this.dataSource = [...pagedItems];
    this.loaded = true;
  }

  onEndSearch(response: IFilterResponse<AdmissionSearchItem>) {
    this.allItems = response.items || [];
    this.searchFilters = response.searchFilters;
    this.selection = new SelectionModel<AdmissionSearchItem>(true, []);
    this.refreshGrid();
    this.fitVisibleColumnsToContainer();
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  changeAppointmentDate(admissionSearchItem: AdmissionSearchItem) {
    const dialogRef = this.dialog.open(
      MedicalAppointmenChangeDateDialogComponent,
      {
        width: "50%",
        data: {},
      }
    );

    dialogRef.afterClosed().subscribe(async (date: Date) => {
      if (date) {
        try {
          await this.admissionService.changeAppointmentDate(
            admissionSearchItem.MedicalAppointmentId,
            date
          );
          this.searchFilter.search();
        } catch (err) {
          this.notificationToastService.error(
            JSON.stringify(err.error),
            "Admisión"
          );
        } finally {
          this.loadingMasiveAction = false;
        }
      }
    });
  }

  assignMedicalAppointment(admissionSearchItem: AdmissionSearchItem) {
    this.router.navigate([
      "reservation-wizard",
      "true",
      admissionSearchItem.MedicalAppointmentId,
      "admission-search",
    ]);
  }

  async patientAdmission() {}

  openMedicalAppointmentObservationDialog(
    admissionSearchItem: AdmissionSearchItem
  ): void {
    const dialogRef = this.dialog.open(
      MedicalAppointmenObservationDialogComponent,
      {
        width: "50%",
        data: {
          medicalAppointmentId: admissionSearchItem.MedicalAppointmentId,
          editLastObservation: true,
        },
      }
    );
  }

  async rollBackControled() {
    try {
      let items = this.selection.selected;
      if (items.length > 0) {
        this.loadingMasiveAction = true;
        let studyReports = this.selection.selected;
        await this.admissionService.rollBackAdmissionControled(
          studyReports.map((s) => s.MedicalAppointmentId)
        );
      }
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Admisión"
      );
    } finally {
      this.loadingMasiveAction = false;
    }
  }

  async controled() {
    try {
      let items = this.selection.selected;
      if (items.length > 0) {
        this.loadingMasiveAction = true;
        let studyReports = this.selection.selected;
        await this.admissionService.setAdmissionControled(
          studyReports.map((s) => s.MedicalAppointmentId)
        );
      }
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Admisión"
      );
    } finally {
      this.loadingMasiveAction = false;
    }
  }

  async admissionEdit(admissionSearchItem: AdmissionSearchItem) {
    this.router.navigate([
      "admission-wizard",
      admissionSearchItem.MedicalAppointmentId,
      admissionSearchItem.Protocol,
      "false",
    ]);
  }

  async admissionView(admissionSearchItem: AdmissionSearchItem) {
    this.router.navigate([
      "admission-wizard",
      admissionSearchItem.MedicalAppointmentId,
      admissionSearchItem.Protocol,
      "true",
    ]);
  }

  openLogViewDialog(admissionSearchItem: AdmissionSearchItem): void {
    const dialogRef = this.dialog.open(LogViewDialogComponent, {
      width: "50%",
      data: admissionSearchItem.MedicalAppointmentId,
    });
  }

  openMedicalAppointmentCancelDialog(
    admissionSearchItem: AdmissionSearchItem
  ): void {
    const medicalAppointmentIds: number[] = [];
    medicalAppointmentIds.push(admissionSearchItem.MedicalAppointmentId);

    const dialogRef = this.dialog.open(
      DialogCancelMedicalAppointmentComponent,
      {
        width: "50%",
        data: { medicalAppointmentIds: medicalAppointmentIds },
      }
    );

    dialogRef.afterClosed().subscribe((result: MedicalAppointmentCancel) => {
      if (result) {
        this.searchFilter.search();
      }
    });
  }
}
